import { useState, useCallback } from 'react';

/**
 * Custom hook for managing templated content editing state and operations
 * Extracts the editing logic from CompleteReview component
 */
const useTemplatedReview = () => {
  // Edited content state for both MCQ and extended questions
  const [editedContent, setEditedContent] = useState({
    mcq: {},
    extended: {}
  });

  /**
   * Handle editing of templated content fields
   * @param {Object} templated - The templated content object with id and type
   * @param {string} field - The field being edited
   * @param {*} value - The new value
   */
  const handleTemplatedEdit = useCallback((templated, field, value) => {
    if (!templated) return;
    
    const key = `${templated.type}_${templated.id}`;
    
    setEditedContent(prev => ({
      ...prev,
      [templated.type]: {
        ...prev[templated.type],
        [key]: {
          ...prev[templated.type][key],
          [field]: value
        }
      }
    }));
  }, []);

  /**
   * Get edited values for a specific content item
   * @param {Object} templated - The templated content object with id and type
   * @returns {Object} The edited values for this content
   */
  const getEditedValues = useCallback((templated) => {
    if (!templated) return {};
    
    const key = `${templated.type}_${templated.id}`;
    return editedContent[templated.type]?.[key] || {};
  }, [editedContent]);

  /**
   * Clear edited values for a specific content item
   * @param {Object} templated - The templated content object with id and type
   */
  const clearEditedValues = useCallback((templated) => {
    if (!templated) return;
    
    const key = `${templated.type}_${templated.id}`;
    
    setEditedContent(prev => {
      const newState = { ...prev };
      if (newState[templated.type]?.[key]) {
        delete newState[templated.type][key];
      }
      return newState;
    });
  }, []);

  /**
   * Get all edited fields for saving
   * @param {Object} templated - The original templated content
   * @returns {Object} Object containing only changed fields
   */
  const getChangedFields = useCallback((templated) => {
    if (!templated) return {};
    
    const edited = getEditedValues(templated);
    const changes = {};

    // For MCQ questions
    if (templated.type === 'mcq') {
      if (edited.question_content !== undefined && edited.question_content !== templated.question_content) {
        changes.question_content = edited.question_content;
      }
      ['option_a', 'option_b', 'option_c', 'option_d'].forEach(field => {
        if (edited[field] !== undefined && edited[field] !== templated[field]) {
          changes[field] = edited[field];
        }
      });
    }

    // For extended questions
    if (templated.type === 'extended') {
      if (edited.question_content !== undefined && edited.question_content !== templated.question_content) {
        changes.question_content = edited.question_content;
      }
      if (!templated.has_parts) {
        if (edited.solution !== undefined && edited.solution !== templated.solution) {
          changes.solution = edited.solution;
        }
      }
    }

    // For extended questions with parts
    if (templated.type === 'extended' && templated.has_parts && templated.parts) {
      const partChanges = {};
      templated.parts.forEach(part => {
        const partContentKey = `part_${part.id}_content`;
        const partAnswerKey = `part_${part.id}_answer`;
        
        if (edited[partContentKey] !== undefined && edited[partContentKey] !== part.part_content) {
          partChanges[partContentKey] = edited[partContentKey];
        }
        if (edited[partAnswerKey] !== undefined && edited[partAnswerKey] !== part.part_answer) {
          partChanges[partAnswerKey] = edited[partAnswerKey];
        }
      });
      
      if (Object.keys(partChanges).length > 0) {
        Object.assign(changes, partChanges);
      }
    }

    return changes;
  }, [getEditedValues]);

  /**
   * Check if there are any unsaved changes
   * @param {Object} templated - The original templated content
   * @returns {boolean} True if there are unsaved changes
   */
  const hasUnsavedChanges = useCallback((templated) => {
    const changes = getChangedFields(templated);
    return Object.keys(changes).length > 0;
  }, [getChangedFields]);

  return {
    editedContent,
    handleTemplatedEdit,
    getEditedValues,
    clearEditedValues,
    getChangedFields,
    hasUnsavedChanges
  };
};

export default useTemplatedReview;